import { Entity, UniqueEntityId } from "@kernel/ddd";
import {
  StatReportDictionary,
  StatsDto,
  StatsReport,
  StatsReportDictionary,
  StatsToDtoEntity
} from "./dto";
import { cloneDeep } from "lodash";
import { DateTime, Duration } from "luxon";

export interface StatsProps {
  roomName: string;
  timestamp: number;
  report: StatsReportDictionary;
}

export class Stats extends Entity<StatsProps> implements StatsToDtoEntity {
  private ticks: Map<string, number> = new Map();
  private intervals: Map<string, NodeJS.Timeout> = new Map();
  public get roomName() {
    return this.props.roomName;
  }

  public get timestamp() {
    return this.props.timestamp;
  }

  public get report() {
    return this.props.report;
  }

  public getUsers(): string[] {
    return Object.keys(this.props.report);
  }

  public getReportForUser(user: string): StatsReport[] | undefined {
    return this.props.report[user];
  }

  public getLastReportDictionary(): StatReportDictionary {
    const users = this.getUsers();
    const lastReports: StatReportDictionary = {};
    users.forEach((user) => {
      const reports = this.getReportForUser(user);
      if (reports) {
        lastReports[user] = reports[reports.length - 1];
      }
    });
    return lastReports;
  }

  public getStatsDuration() {
    const firstUser = this.getUsers()[0];
    const lastUser = this.getUsers()[this.getUsers().length - 1];
    if (!firstUser || !lastUser) {
      return Duration.fromMillis(0, { locale: "it" });
    }
    const firstReport = this.getReportForUser(firstUser)![0];
    const lastReport =
      this.getReportForUser(lastUser)![
        this.getReportForUser(lastUser)!.length - 1
      ];
    const lastDate = DateTime.fromMillis(lastReport.timestamp, {
      locale: "it"
    });
    const firstDate = DateTime.fromMillis(firstReport.timestamp, {
      locale: "it"
    });
    return lastDate.diff(firstDate, ["hours", "minutes", "seconds"]);
  }

  public startTicks(user: string, callback: (report: StatsReport) => void) {
    this.firstTick(user, callback);
    const userReports = this.getReportForUser(user);
    const intervalId = setInterval(() => {
      let index = 1;
      if (this.ticks.has(user)) {
        index = this.ticks.get(user) as number;
      }
      if (!userReports) {
        return;
      }
      this.ticks.set(user, index + 1);
      if (index < userReports.length) {
        callback(userReports[index]);
      } else {
        clearInterval(intervalId);
      }
    }, 5000);
    this.intervals.set(user, intervalId);
  }

  private firstTick(user: string, callback: (report: StatsReport) => void) {
    const userReports = this.getReportForUser(user);
    if (!userReports) {
      return;
    }
    callback(userReports[0]);
  }

  public stopTicks(user: string) {
    const intervalId = this.intervals.get(user);
    if (intervalId) {
      clearInterval(intervalId);
    }
  }

  public clone(): Stats {
    const props = cloneDeep(this.props);
    return new Stats(props, new UniqueEntityId(this._id.toValue()));
  }

  public toString(): string {
    return JSON.stringify(this.props);
  }

  public toDto(): StatsDto {
    return {
      id: this._id.toString(),
      roomName: this.roomName,
      timestamp: this.timestamp,
      report: this.report
    };
  }

  public static create(props: StatsProps, id?: UniqueEntityId) {
    return new Stats(props, id);
  }

  /**
   * Format bytes as human-readable text.
   *
   * @param bytes Number of bytes.
   * @param si True to use metric (SI) units, aka powers of 1000. False to use
   *           binary (IEC), aka powers of 1024.
   * @param dp Number of decimal places to display.
   *
   * @return Formatted string.
   */
  public static humanFileSize(bytes: number, si = false, dp = 1) {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
      return bytes + " B";
    }

    const units = si
      ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
      : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
    let u = -1;
    const r = 10 ** dp;

    do {
      bytes /= thresh;
      ++u;
    } while (
      Math.round(Math.abs(bytes) * r) / r >= thresh &&
      u < units.length - 1
    );

    return bytes.toFixed(dp) + " " + units[u];
  }
}
